import { useState, useEffect, useCallback } from 'react';
import { useAnalysisSharing } from '../../hooks/useAnalysisSharing';
import { useTranslation } from 'react-i18next';

interface ShareAnalysisDialogProps {
  sessionId: string;
  onClose: () => void;
}

export function ShareAnalysisDialog({ sessionId, onClose }: ShareAnalysisDialogProps) {
  const { shareAnalysis, unshareAnalysis, checkAnalysisShared, updateShareOptions, isLoading } = useAnalysisSharing();

  const [isShared, setIsShared] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [includeFollowups, setIncludeFollowups] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Check if already shared on mount
  useEffect(() => {
    const checkStatus = async () => {
      setIsChecking(true);
      const result = await checkAnalysisShared(sessionId);
      if (result?.isShared) {
        setIsShared(true);
        setShareUrl(result.shareUrl || null);
        setIncludeFollowups(result.includeFollowups ?? true);
      }
      setIsChecking(false);
    };
    checkStatus();
  }, [sessionId, checkAnalysisShared]);

  const handleShare = useCallback(async () => {
    setError(null);
    const result = await shareAnalysis(sessionId, { includeFollowups });
    if (result) {
      setIsShared(true);
      setShareUrl(result.shareUrl);
    } else {
      setError(t('shareAnalysisDialog.shareError'));
    }
  }, [sessionId, includeFollowups, shareAnalysis]);

  const handleUnshare = useCallback(async () => {
    setError(null);
    const success = await unshareAnalysis(sessionId);
    if (success) {
      setIsShared(false);
      setShareUrl(null);
    } else {
      setError(t('shareAnalysisDialog.unshareError'));
    }
  }, [sessionId, unshareAnalysis]);

  const handleToggleFollowups = useCallback(async () => {
    const newValue = !includeFollowups;
    setIncludeFollowups(newValue);

    if (isShared) {
      const success = await updateShareOptions(sessionId, { includeFollowups: newValue });
      if (!success) {
        // Revert on failure
        setIncludeFollowups(!newValue);
        setError(t('shareAnalysisDialog.updateError'));
      }
    }
  }, [includeFollowups, isShared, sessionId, updateShareOptions]);

  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  if (isChecking) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center gap-3">
          <svg className="w-5 h-5 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-slate-600 dark:text-slate-400">{t('shareAnalysisDialog.checking')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('shareAnalysisDialog.title')}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('shareAnalysisDialog.subtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Share URL (if shared) */}
      {isShared && shareUrl && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('shareAnalysisDialog.linkLabel')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 text-sm"
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                copied
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('shareAnalysisDialog.copied')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  {t('shareAnalysisDialog.copy')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={handleToggleFollowups}
            className={`relative w-10 h-6 rounded-full transition-colors ${
              includeFollowups ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <div
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                includeFollowups ? 'left-5' : 'left-1'
              }`}
            />
          </div>
          <div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('shareAnalysisDialog.includeFollowups')}
            </span>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('shareAnalysisDialog.followupsDesc')}
            </p>
          </div>
        </label>
      </div>

      {/* Info */}
      <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('shareAnalysisDialog.includesTitle')}
        </h4>
        <ul className="space-y-1 text-sm text-slate-500 dark:text-slate-400">
          <li className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('shareAnalysisDialog.includeDocument')}
          </li>
          <li className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('shareAnalysisDialog.includeClarifications')}
          </li>
          <li className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('shareAnalysisDialog.includeReport')}
          </li>
          {includeFollowups && (
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('shareAnalysisDialog.includeFollowupsItem')}
            </li>
          )}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        {isShared ? (
          <>
            <button
              type="button"
              onClick={handleUnshare}
              disabled={isLoading}
              className="px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium transition-colors"
            >
              {t('shareAnalysisDialog.unshare')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors"
            >
              {t('shareAnalysisDialog.close')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 text-sm font-medium transition-colors"
            >
              {t('shareAnalysisDialog.cancel')}
            </button>
            <button
              type="button"
              onClick={handleShare}
              disabled={isLoading}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('shareAnalysisDialog.sharing')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  {t('shareAnalysisDialog.share')}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ShareAnalysisDialog;
